import { httpClient } from '@/services/http/httpClient'
import { getQueryParams, type QueryParams } from '@/utils/queryParams'

import type { IdeaStatus } from '../types'

type IdeaHttpResponse = {
  id: string
  title: string
  description: string
  sequence: string
  ideaStatuses: {
    name: string
    customName: string | null
    color: string
    customColor: string | null
  }
  likes: number
  view: number
  _count: {
    ideaView: number
    forumComment: number
  }
  ideaUsers: {
    user: {
      id: string
      name: string
      email: string
      image?: string
      status?: string
      area?: {
        id: string
        name: string
      }
    }
  }[]
  campaign: {
    id: string
    sequence: number
    title: string
  }
  isLiked: boolean
}

type IdeaFeedHttpResponse = {
  ideas: IdeaHttpResponse[]
  paginate: {
    total: number
    page: number
    pages: number
    limit: number
  }
}

type IdeaFeedParams = {
  query: QueryParams
}

export type IdeaFeed = {
  id: string
  sequence: string
  title: string
  description: string
  status: IdeaStatus
  statusColor?: string
  likes: number
  views: number
  comments: number
  participants: {
    id: string
    name: string
    email: string
    image?: string
    status?: string
    area?: {
      id: string
      name: string
    }
  }[]
  challenge: {
    id: string
    sequence: string
    title: string
  }
  isLiked?: boolean
}

type IdeaFeedResponse = {
  ideas: IdeaFeed[]
  pagination: {
    total: number
    page: number
    pages: number
    limit: number
  }
}

export async function getFeed({
  query,
}: IdeaFeedParams): Promise<IdeaFeedResponse> {
  const queryParams = getQueryParams(query)

  const { data } = await httpClient.get<IdeaFeedHttpResponse>(
    `ideas/ideas/feed?${queryParams}`,
  )

  // todo: verificar dados de retorno do endpoint
  return {
    ideas: data.ideas.map(
      (idea) =>
        ({
          id: idea.id,
          sequence: idea.sequence,
          title: idea.title,
          description: idea.description,
          status: idea.ideaStatuses.name,
          statusColor: idea.ideaStatuses.customColor ?? idea.ideaStatuses.color,
          likes: idea.likes,
          views: idea._count.ideaView,
          comments: idea._count.forumComment,
          isLiked: idea.isLiked,
          participants: idea.ideaUsers.map((ideaUser) => ({
            id: ideaUser.user.id,
            name: ideaUser.user.name,
            email: ideaUser.user.email,
            image: ideaUser.user.image,
            area: ideaUser.user.area,
            status: ideaUser.user.status,
          })),
          challenge: {
            id: idea.campaign.id,
            sequence: `#${String(idea.campaign.sequence).padStart(2, '0')}`,
            title: idea.campaign.title,
          },
        }) as IdeaFeed,
    ),
    pagination: {
      total: data.paginate.total,
      limit: data.paginate.limit,
      page: data.paginate.page,
      pages: data.paginate.pages,
    },
  }
}
